// A meeting — a "пространство" — as a server of Discord, dressed as Veyumo: on the left, dark, the logo, the
// meeting's menu (inviting, the account's other meetings, a new one, leaving, the host's closing it), its
// text channels, its voice channels — how many are in each, whether a transcript of it is written — its
// participants, the call while the user reads elsewhere, and the user. In the middle, light, the channel
// shown under its name and where the user is: a text channel's chat, or a voice channel — who is in it and
// joining it, or in the call its tiles, who speaks ringed, and the round buttons of the microphone, the
// camera, the screen, the board, the transcript and leaving below — the tiles a page of `SLOTS` at a time, or
// the one who speaks large and the others in a row below. On the right the voice channel's transcripts, which
// anyone in the meeting reads without joining. A screen shown and a board of the call (Board.tsx) take the
// middle, whole, the tiles in a row below them. The host makes, renames and deletes channels and makes
// moderators. On a phone one pane at a time: the channels and participants come over the page, the transcript
// takes the tiles' place, and a strip above the middle leads back to the call.
import type { RefObject } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { Board } from "../board.ts";
import { boardsShown, openBoard } from "../boards.ts";
import { camera, canShare, deafen, microphone, screen, screensShown, showTiles, SLOTS, videoOf, type Shown } from "../call.ts";
import { layout, TILE_GAP, type Grid } from "../layout.ts";
import {
  addChannel,
  askMine,
  deleteChannel,
  host,
  invite,
  joinVoice,
  leave,
  leaveCall,
  moderate,
  pane,
  renameChannel,
  showTranscript,
  startNew,
  switchTo,
  view,
} from "../meeting.ts";
import {
  changed,
  channelName,
  channelOf,
  isHost,
  isModerator,
  nameOf,
  state,
  transcriptsOf,
  type CallView,
  type Channel,
} from "../store.ts";
import { Status } from "./App.tsx";
import { Avatar } from "./Avatar.tsx";
import { BoardTile } from "./Board.tsx";
import { Chat } from "./Chat.tsx";
import { Icon, IconButton, type IconName } from "./Icon.tsx";
import { Transcripts } from "./Transcript.tsx";

/** A phone's width: one pane at a time. */
const NARROW = "(max-width: 720px)";
/** A width without room for the panel on the right: a voice channel's transcripts take the middle. */
const COMPACT = "(max-width: 1100px)";

/** Whether the page is a phone's: one pane at a time. */
function phone(): boolean {
  return matchMedia(NARROW).matches;
}

/** Whether the page is too narrow for the panel on the right. */
function compact(): boolean {
  return matchMedia(COMPACT).matches;
}

/** Whether the middle shows the voice channel of the user's call. */
function inCallView(): boolean {
  const call = state.call;
  return call !== null && state.view.kind === "voice" && state.view.channel === call.channel;
}

export function MeetingView() {
  // A window narrowed past a width that matters, or widened past it, lays the meeting out again.
  useEffect(() => {
    const qs = [matchMedia(NARROW), matchMedia(COMPACT)];
    const turned = () => changed();
    for (const q of qs) q.addEventListener("change", turned);
    return () => {
      for (const q of qs) q.removeEventListener("change", turned);
    };
  }, []);
  const v = state.view;
  const inCall = inCallView();
  const narrow = compact();
  // A voice channel's transcripts: on a wide screen the panel on the right — in the call while the user
  // keeps it open; on a narrow one in the middle (VoiceView).
  const panel = v.kind === "voice" && !narrow && (!inCall || state.panel);
  const classes = [
    "meeting",
    v.kind === "text" ? "view-text" : inCall ? "in-call" : "view-voice",
    `pane-${state.pane}`,
    panel ? "panel-open" : "panel-closed",
  ];
  return (
    <div class={classes.join(" ")}>
      <Sidebar />
      <main class="main">
        <MainHead />
        <Status />
        <CallStrip />
        <div class="content">
          {v.kind === "text" ? (
            <Chat key={v.channel} />
          ) : (
            <VoiceView key={v.channel} channel={v.channel} inCall={inCall} narrow={narrow} />
          )}
        </div>
      </main>
      {panel && (
        <aside class="panel">
          <Transcripts key={v.channel} channel={v.channel} />
        </aside>
      )}
    </div>
  );
}

/** `ids`, the host first, the others by name. */
function sorted(ids: number[]): number[] {
  const first = state.meeting!.host;
  return ids.sort((a, b) => (a === first ? -1 : b === first ? 1 : nameOf(a).localeCompare(nameOf(b), "ru")));
}

/** Who is in voice channel `channel`, each once — the user too, from joining it on. */
function voiceUsers(channel: number): number[] {
  const ids = new Set(state.voices.get(channel)?.users ?? []);
  if (state.call?.channel === channel) ids.add(state.user);
  else ids.delete(state.user);
  return sorted([...ids]);
}

/** The voice channel user `id` is in, if any. */
function voiceOf(id: number): Channel | null {
  if (id === state.user) return state.call === null ? null : channelOf(state.call.channel, "voice");
  const voices = state.layout.channels.filter((c) => c.kind === "voice");
  return voices.find((c) => state.voices.get(c.number)?.users.includes(id)) ?? null;
}

/** Whether a transcript of voice channel `channel` is written now. */
function recorded(channel: number): boolean {
  return (state.transcripts.get(channel)?.writing ?? 0) !== 0;
}

/** `n` and the word for it in Russian: "1 участник", "2 участника", "5 участников". */
function counted(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  const word = d === 1 && h !== 11 ? one : d >= 2 && d <= 4 && (h < 12 || h > 14) ? few : many;
  return `${n} ${word}`;
}

/** The transcript of voice channel `channel`: the channel shows, and the transcript written in it — on a
 * narrow page in the call its pane, on a wide one the panel beside the tiles. */
function openTranscript(channel: number): void {
  view("voice", channel);
  const t = transcriptsOf(channel);
  if (t.writing !== 0 && t.shown !== t.writing) showTranscript(channel, t.writing);
  if (state.call?.channel !== channel) return;
  if (compact()) return pane("chat");
  state.panel = true;
  changed();
}

/** Whether the transcript shows in the call: a narrow page's pane, or the panel beside the tiles. */
function transcriptShown(): boolean {
  return compact() ? state.pane === "chat" : state.panel;
}

/** The transcript in the call shown, or — shown already — hidden. */
function toggleTranscript(): void {
  if (compact()) return pane(state.pane === "chat" ? "voice" : "chat");
  state.panel = !state.panel;
  changed();
}

/** The dialog of the user's name, picture and access code. */
function openProfile(): void {
  state.dialog = { kind: "profile" };
  changed();
}

/** The logo, the meeting's menu, the channels, the participants, the call and the user. */
function Sidebar() {
  const owner = isHost();
  const texts = state.layout.channels.filter((c) => c.kind === "text");
  const voices = state.layout.channels.filter((c) => c.kind === "voice");
  const online = sorted([...state.online]);
  return (
    <aside class="side">
      <div class="side-brand">
        <img class="logo" src="/logo.png" alt="Veyumo" width={150} height={32} />
        <IconButton
          icon="close"
          class="only-phone"
          title="Закрыть"
          onClick={() => pane(inCallView() ? "voice" : "chat")}
        />
      </div>
      <MeetingMenu />
      <div class="side-body">
        <Section title="Текстовые каналы" add={owner ? "text" : null} />
        {texts.map((c) => (
          <ChannelRow key={c.number} c={c} />
        ))}
        <Section title="Голосовые каналы" add={owner ? "voice" : null} />
        {voices.map((c) => (
          <ChannelRow key={c.number} c={c} />
        ))}
        <div class="side-section">
          <span>Участники — {online.length}</span>
        </div>
        {online.map((id) => (
          <Member key={id} id={id} />
        ))}
      </div>
      <CallStrip />
      <UserPanel />
    </aside>
  );
}

/** The meeting's name, and under it its menu: inviting, the account's other meetings, a new one, leaving
 * — and for the host closing it for everyone. */
function MeetingMenu() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => {
      if (box.current !== null && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("keydown", key);
    };
  }, [open]);
  const m = state.meeting!;
  const others = state.mine.filter((x) => x.id !== m.id && !x.ended).slice(0, 5);
  /** What an item does, the menu closed first. */
  const act = (f: () => void) => () => {
    setOpen(false);
    f();
  };
  return (
    <div class="meeting-menu" ref={box}>
      <button
        class={open ? "meeting-pick open" : "meeting-pick"}
        aria-haspopup="menu"
        aria-expanded={open}
        title={m.title}
        onClick={() => {
          if (!open) askMine();
          setOpen(!open);
        }}
      >
        <span>{m.title}</span>
        <Icon name="chevronDown" size={18} />
      </button>
      {open && (
        <div class="menu" role="menu">
          <MenuItem icon="userPlus" label="Пригласить" onClick={act(invite)} />
          {others.length > 0 && <div class="menu-head">Мои пространства</div>}
          {others.map((x) => (
            <MenuItem key={x.id} icon="hash" label={x.title} onClick={act(() => switchTo(x.id))} />
          ))}
          <MenuItem icon="plus" label="Новое пространство" onClick={act(startNew)} />
          <div class="menu-line" role="separator" />
          <MenuItem icon="exit" label="Выйти из пространства" onClick={act(() => leave())} />
          {isHost() && (
            <MenuItem icon="power" label="Закрыть для всех" danger onClick={act(() => host("end", 0))} />
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, danger, onClick }: { icon: IconName; label: string; danger?: boolean; onClick: () => void }) {
  return (
    <button class={danger ? "menu-item danger" : "menu-item"} role="menuitem" onClick={onClick}>
      <Icon name={icon} size={18} />
      <span>{label}</span>
    </button>
  );
}

/** The head of a list of channels: for the host, the button that makes one of its kind. */
function Section({ title, add }: { title: string; add: "text" | "voice" | null }) {
  return (
    <div class="side-section">
      <span>{title}</span>
      {add !== null && (
        <IconButton
          icon="plus"
          title={add === "text" ? "Создать текстовый канал" : "Создать голосовой канал"}
          onClick={() => addChannel(add)}
        />
      )}
    </div>
  );
}

/** A channel: a click shows it. A voice channel says how many are in it — who, under the pointer — and
 * whether its transcript is written; a text channel how often the user was named in it. For the host the
 * buttons to rename and delete a channel — but a meeting's first two, which stay — under the pointer, or
 * always on a phone. */
function ChannelRow({ c }: { c: Channel }) {
  const m = state.meeting!;
  const voice = c.kind === "voice";
  const name = channelName(c);
  const active = state.view.kind === c.kind && state.view.channel === c.number;
  const users = voice ? voiceUsers(c.number) : [];
  const rec = voice && recorded(c.number);
  const mentioned = voice ? 0 : (state.mentions.get(c.number) ?? 0);
  const title = [name, rec && "идёт расшифровка", users.length > 0 && `в канале: ${users.map(nameOf).join(", ")}`]
    .filter((x) => x)
    .join(" · ");
  return (
    <div class={active ? "channel active" : "channel"}>
      <button class="channel-open" title={title} onClick={() => view(c.kind, c.number)}>
        <Icon name={voice ? "volume" : "hash"} />
        <span class="channel-name">{name}</span>
        {mentioned > 0 && (
          <span class="badge" title={`Вас упомянули: ${mentioned}`}>
            {mentioned > 99 ? "99+" : mentioned}
          </span>
        )}
        {rec && <span class="rec on" />}
        {users.length > 0 && <span class="count">{users.length}</span>}
      </button>
      {isHost() && (
        <span class="acts">
          <IconButton icon="pencil" title="Переименовать" onClick={() => renameChannel(c.number, c.kind, name)} />
          {c.number !== m.number && (
            <IconButton
              icon="trash"
              class="danger"
              title="Удалить канал"
              onClick={() => deleteChannel(c.number, c.kind, name)}
            />
          )}
        </span>
      )}
    </div>
  );
}

/** A participant: the host's and the moderators' marks, whether in a voice channel, and for the host the
 * buttons to make it a moderator or not, mute it and remove it (under the pointer, or always on a
 * phone). */
function Member({ id }: { id: number }) {
  const m = state.meeting!;
  const own = id === state.user;
  const where = voiceOf(id);
  const mod = isModerator(id);
  return (
    <div class={state.speaking.has(id) ? "member speaking" : "member"}>
      <Avatar id={id} size={36} />
      <span class="name">{own ? `${nameOf(id)} (вы)` : nameOf(id)}</span>
      {id === m.host && (
        <span class="mark" title="Владелец">
          <Icon name="crown" size={18} />
        </span>
      )}
      {mod && (
        <span class="mark" title="Модератор: ведёт расшифровки">
          <Icon name="shield" size={18} />
        </span>
      )}
      {where !== null && (
        <span class="mark" title={`В голосовом канале «${channelName(where)}»`}>
          <Icon name="volume" size={18} />
        </span>
      )}
      {isHost() && !own && (
        <span class="acts">
          <IconButton
            icon="shield"
            class={mod ? "lit" : ""}
            title={mod ? "Снять модератора" : "Сделать модератором: ведёт расшифровки"}
            pressed={mod}
            onClick={() => moderate(id, !mod)}
          />
          {where !== null && <IconButton icon="micOff" title="Выключить микрофон" onClick={() => host("mute", id)} />}
          <IconButton icon="userX" class="danger" title="Удалить из пространства" onClick={() => host("remove", id)} />
        </span>
      )}
    </div>
  );
}

/** The user, at the bottom of the left: picture, name and whether connected; the others' sound off or on,
 * and the settings — the name, the picture and the access code. */
function UserPanel() {
  const deaf = state.deafened;
  return (
    <div class="user-panel">
      <button class="me" title="Профиль: имя и аватар" onClick={openProfile}>
        <Avatar id={state.user} size={40} />
        <span class="me-text">
          <b>{nameOf(state.user)}</b>
          <small class={state.up ? "state on" : "state warn"}>
            <i class="dot" />
            {state.up ? "Подключено" : "Нет связи"}
          </small>
        </span>
      </button>
      <IconButton
        icon={deaf ? "headphonesOff" : "headphones"}
        class={deaf ? "off" : ""}
        title={deaf ? "Включить звук собеседников" : "Выключить звук собеседников"}
        pressed={deaf}
        onClick={() => deafen(!deaf)}
      />
      <IconButton icon="settings" title="Настройки: имя, аватар, код доступа" onClick={openProfile} />
      <IconButton icon="chevronRight" class="more" title="Профиль" onClick={openProfile} />
    </div>
  );
}

/** Above the middle: the channel shown, the meeting and where the user is in it; the channels and
 * participants on a phone; whether the call is transcribed; inviting. */
function MainHead() {
  const m = state.meeting!;
  const v = state.view;
  const c = channelOf(v.channel, v.kind);
  const call = state.call;
  const rec = call !== null && recorded(call.channel) ? call.channel : -1;
  const [tone, where] = !state.up
    ? ["warn", "нет связи, соединяюсь…"]
    : v.kind === "voice"
      ? call?.channel === v.channel
        ? ["on", "Подключено"]
        : ["idle", "Не подключено"]
      : ["on", `${state.online.size} в сети`];
  return (
    <header class="main-head">
      <IconButton icon="menu" class="only-phone" title="Каналы и участники" onClick={() => pane("members")} />
      <div class="head-title">
        <b>
          <Icon name={v.kind === "text" ? "hash" : "volume"} size={24} />
          <span>{c === null ? "" : channelName(c)}</span>
        </b>
        <small class="crumbs">
          <span class="crumb">{m.title}</span>
          <Icon name="chevronRight" size={14} />
          <span class={`state ${tone}`}>
            <i class="dot" />
            {where}
          </span>
        </small>
      </div>
      {rec >= 0 && (
        <button
          class="rec-badge"
          title="В вашем голосовом канале идёт расшифровка: открыть"
          onClick={() => openTranscript(rec)}
        >
          <span class="rec on" />
          <span class="label">расшифровка</span>
        </button>
      )}
      <button class="invite" title="Пригласить: ссылка на пространство" onClick={invite}>
        <Icon name="userPlus" />
        <span class="label">Пригласить</span>
      </button>
    </header>
  );
}

/** While the middle shows another channel than the call's: the call's channel, a click away, its
 * microphone and leaving it — on a phone above the middle, on a wide screen above the user. */
function CallStrip() {
  const call = state.call;
  if (call === null || inCallView()) return null;
  const c = channelOf(call.channel, "voice");
  return (
    <div class="call-strip">
      <button class="call-back" title="К звонку" onClick={() => view("voice", call.channel)}>
        <Icon name="volume" size={18} />
        <span class="call-text">
          <b>Голос подключён</b>
          <small>{c === null ? "голосовой канал" : channelName(c)}</small>
        </span>
      </button>
      <MicButton />
      <IconButton icon="hangup" class="danger" title="Отключиться от голосового канала" onClick={leaveCall} />
    </div>
  );
}

/** The microphone of a strip: off — red and crossed out, as it matters most. */
function MicButton() {
  const off = state.micOff;
  return (
    <IconButton
      icon={off ? "micOff" : "mic"}
      class={off ? "off" : ""}
      title={off ? "Включить микрофон" : "Выключить микрофон"}
      pressed={!off}
      onClick={() => microphone(off)}
    />
  );
}

/** A voice channel: how many are in it; who they are and joining it, or in the call its tiles and its
 * bar. On a narrow page its transcripts below whom it has — in the call in the tiles' place, when asked
 * for. */
function VoiceView({ channel, inCall, narrow }: { channel: number; inCall: boolean; narrow: boolean }) {
  const users = voiceUsers(channel);
  const transcript = narrow && (!inCall || state.pane === "chat");
  const people = !narrow || !inCall || state.pane !== "chat";
  return (
    <section class="voice">
      {people && (
        <div class="voice-head">
          <span>Голосовая встреча</span>
          <div class="voice-tools">
            {inCall && screensShown().length === 0 && boardsShown().length === 0 && <ViewSwitch count={users.length} />}
            <span class="muted">{counted(users.length, "участник", "участника", "участников")}</span>
          </div>
        </div>
      )}
      {people && (inCall ? <People /> : <Lobby channel={channel} users={users} />)}
      {transcript && <Transcripts key={channel} channel={channel} />}
      {inCall && <ControlBar channel={channel} />}
    </section>
  );
}

/** How the call shows its people: tiles, a page of `SLOTS` at a time — the pages' arrows when there are
 * more —, or the one who speaks large. */
function ViewSwitch({ count }: { count: number }) {
  const grid = state.callView === "grid";
  const pages = Math.max(1, Math.ceil(count / SLOTS));
  const page = Math.min(state.page, pages - 1);
  const go = (p: number) => {
    state.page = Math.max(0, Math.min(pages - 1, p));
    changed();
  };
  const set = (v: CallView) => {
    state.callView = v;
    changed();
  };
  return (
    <div class="view-switch">
      {grid && pages > 1 && (
        <div class="pages">
          <IconButton icon="chevronLeft" title="Предыдущая страница" onClick={() => go(page - 1)} />
          <span>
            {page + 1} / {pages}
          </span>
          <IconButton icon="chevronRight" title="Следующая страница" onClick={() => go(page + 1)} />
        </div>
      )}
      <IconButton icon="grid" title="Сетка" class={grid ? "lit" : ""} pressed={grid} onClick={() => set("grid")} />
      <IconButton
        icon="speaker"
        title="Говорящий крупно"
        class={grid ? "" : "lit"}
        pressed={!grid}
        onClick={() => set("speaker")}
      />
    </div>
  );
}

/** A voice channel the user is not in: who is there, and joining it — leaving the call elsewhere. */
function Lobby({ channel, users }: { channel: number; users: number[] }) {
  const c = channelOf(channel, "voice");
  const faces = users.slice(0, 5);
  return (
    <div class="lobby">
      <div class="lobby-faces">
        {faces.length === 0 ? (
          <span class="lobby-icon">
            <Icon name="headphones" size={40} />
          </span>
        ) : (
          faces.map((id) => <Avatar key={id} id={id} size={72} />)
        )}
        {users.length > faces.length && <span class="lobby-more">+{users.length - faces.length}</span>}
      </div>
      <b class="lobby-title">{c === null ? "" : channelName(c)}</b>
      <span class="lobby-who">
        {users.length === 0 ? "В канале пока никого — зайдите первым" : `В канале: ${users.map(nameOf).join(", ")}`}
      </span>
      <button class="primary join" onClick={() => joinVoice(channel)}>
        <Icon name="headphones" />
        <span>{state.call === null ? "Подключиться" : "Перейти сюда"}</span>
      </button>
    </div>
  );
}

/** The call's bar: the microphone, the camera, the screen (where the browser can show one), a new board,
 * the transcript — a red dot while it is written — and leaving the voice channel; each a round button over
 * its name. */
function ControlBar({ channel }: { channel: number }) {
  const narrow = compact();
  const shown = transcriptShown();
  const off = state.micOff;
  const cam = state.camOn;
  const sharing = state.call?.share != null;
  const back = narrow && shown;
  return (
    <div class="control-bar">
      <Control
        icon={off ? "micOff" : "mic"}
        label="Микрофон"
        title={off ? "Включить микрофон" : "Выключить микрофон"}
        tone={off ? "off" : ""}
        pressed={!off}
        onClick={() => microphone(off)}
      />
      <Control
        icon={cam ? "camera" : "cameraOff"}
        label="Камера"
        title={cam ? "Выключить камеру" : "Включить камеру"}
        tone={cam ? "on" : ""}
        pressed={cam}
        onClick={() => void camera(!cam)}
      />
      {canShare() && (
        <Control
          icon="screen"
          label="Экран"
          title={sharing ? "Перестать показывать экран" : "Показать экран"}
          tone={sharing ? "on" : ""}
          pressed={sharing}
          onClick={() => void screen(!sharing)}
        />
      )}
      <Control icon="board" label="Доска" title="Открыть доску для всех в звонке" tone="" onClick={openBoard} />
      <Control
        icon={back ? "grid" : "captions"}
        label={back ? "Участники" : "Расшифровка"}
        title={narrow ? (shown ? "К участникам" : "Расшифровка") : shown ? "Скрыть расшифровки" : "Показать расшифровки"}
        tone={shown ? "on" : ""}
        pressed={shown}
        rec={recorded(channel)}
        onClick={toggleTranscript}
      />
      <Control icon="hangup" label="Выйти" title="Отключиться от голосового канала" tone="leave" onClick={leaveCall} />
    </div>
  );
}

function Control({
  icon,
  label,
  title,
  tone,
  pressed,
  rec,
  onClick,
}: {
  icon: IconName;
  label: string;
  title: string;
  tone: "" | "on" | "off" | "leave";
  pressed?: boolean;
  rec?: boolean;
  onClick: () => void;
}) {
  return (
    <button class={`control ${tone}`} title={title} aria-label={title} aria-pressed={pressed} onClick={onClick}>
      <span class="control-icon">
        <Icon name={icon} size={24} />
        {rec && <span class="rec on" />}
      </span>
      <span class="control-label">{label}</span>
    </button>
  );
}

/** The tiles of those in the call's voice channel, filling their pane as layout.ts lays them out; the
 * host first, the others by name. A screen shown or a board: the stage above, the tiles in a row below it;
 * or, so asked, the one who speaks large above them. */
function People() {
  const shown = screensShown();
  const boards = boardsShown();
  if (shown.length > 0 || boards.length > 0) return <Stage shown={shown} boards={boards} />;
  return state.callView === "speaker" ? <SpeakerStage /> : <Tiles />;
}

/** The height of a tile of the row below a stage (`.strip`), pixels. */
const STRIP_TILE = 116;

/** Users `ids` by importance — who speaks large first —, as the server shares the way to the browser
 * among their pictures. */
function important(ids: number[]): number[] {
  return ids.includes(state.speaker) ? [state.speaker, ...ids.filter((id) => id !== state.speaker)] : ids;
}

/** The users whose tiles in `pane` are on the screen, a part at least (null until that is known): of the
 * others the page asks no picture. */
function useVisible(pane: RefObject<HTMLDivElement | null>, ids: number[]): Set<number> | null {
  const [seen, setSeen] = useState<Set<number> | null>(null);
  const key = ids.join(",");
  useLayoutEffect(() => {
    const root = pane.current;
    if (root === null) return;
    const now = new Set<number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = Number((e.target as HTMLElement).dataset.user);
          if (e.isIntersecting) now.add(id);
          else now.delete(id);
        }
        setSeen(new Set(now));
      },
      { root },
    );
    root.querySelectorAll<HTMLElement>(".tile[data-user]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [key]);
  return seen;
}

/** The boards and the screens shown — each whole, side by side — and below them the tiles, a row that
 * scrolls: small pictures of the cameras, of those in sight. */
function Stage({ shown, boards }: { shown: Shown[]; boards: Board[] }) {
  const ids = voiceUsers(state.call!.channel);
  const strip = useRef<HTMLDivElement>(null);
  const visible = useVisible(strip, ids);
  useEffect(() => {
    if (visible === null) return;
    const small = important(ids.filter((id) => visible.has(id)));
    showTiles(small, small.map(() => STRIP_TILE));
  });
  return (
    <div class="people with-screen">
      <div class="stage">
        {boards.map((b) => (
          <BoardTile key={`board-${b.number}`} b={b} />
        ))}
        {shown.map((s) => (
          <ScreenTile key={s.user} shown={s} />
        ))}
      </div>
      <div class="strip" ref={strip}>
        {ids.map((id) => (
          <Tile key={id} id={id} />
        ))}
      </div>
    </div>
  );
}

/** The one who speaks large — who of the others spoke last, or the first of them — and below it the
 * others, a row that scrolls: small pictures of the cameras, of those in sight. */
function SpeakerStage() {
  const ids = voiceUsers(state.call!.channel);
  const main = ids.includes(state.speaker) ? state.speaker : (ids.find((id) => id !== state.user) ?? ids[0]);
  const rest = ids.filter((id) => id !== main);
  const stage = useRef<HTMLDivElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const el = stage.current!;
    const measure = () => setHeight(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const visible = useVisible(strip, rest);
  useEffect(() => {
    if (visible === null || height === 0 || main === undefined) return;
    const small = important(rest.filter((id) => visible.has(id)));
    showTiles([main, ...small], [height, ...small.map(() => STRIP_TILE)]);
  });
  return (
    <div class="people with-screen speaker-view">
      <div class="stage" ref={stage}>
        {main !== undefined && <Tile key={main} id={main} />}
      </div>
      <div class="strip" ref={strip}>
        {rest.map((id) => (
          <Tile key={id} id={id} />
        ))}
      </div>
    </div>
  );
}

/** A screen shown: its picture, whole, and whose it is. */
function ScreenTile({ shown }: { shown: Shown }) {
  const own = shown.user === state.user;
  return (
    <div class="screen-tile" data-user={shown.user}>
      {shown.el !== null ? <Mount el={shown.el} /> : <span class="empty">экран загружается…</span>}
      <div class="name">
        <Icon name="screen" size={14} />
        <span>{own ? "Ваш экран" : `Экран: ${nameOf(shown.user)}`}</span>
      </div>
    </div>
  );
}

function Tiles() {
  const pane = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<[number, number]>([0, 0]);
  // The pane's size, without its padding: the tiles follow it — a window resized, a phone turned, the
  // transcript shown or hidden. A pixel less than the browser says: its sizes are whole pixels, rounded —
  // up, at times, on a screen whose scale is not a whole number (125 %, 150 %) — and a grid a fraction of
  // a pixel larger than its pane would make it scroll, which would narrow it, which would make the tiles
  // smaller, which would end the scrolling, and so on, the tiles trembling.
  useLayoutEffect(() => {
    const el = pane.current!;
    const measure = () => {
      const cs = getComputedStyle(el);
      const w = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - 1;
      const h = el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - 1;
      setSize((old) => (old[0] === w && old[1] === h ? old : [w, h]));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // A page of tiles: `SLOTS`, as many as the call takes pairs of tracks.
  const all = voiceUsers(state.call!.channel);
  const pages = Math.max(1, Math.ceil(all.length / SLOTS));
  const page = Math.min(state.page, pages - 1);
  const ids = all.slice(page * SLOTS, (page + 1) * SLOTS);
  const visible = useVisible(pane, ids);
  const [w, h] = size;
  // A pane not shown (a phone shows the transcript) is laid out when it is.
  const g: Grid | null = ids.length > 0 && w > 0 && h > 0 ? layout(ids.length, w, h, phone()) : null;
  // The avatar of a tile without a picture: large in a large tile, room left for the name below it.
  const style =
    g === null
      ? ""
      : `width:${g.cols * g.w + (g.cols - 1) * TILE_GAP}px;--tile-w:${g.w}px;--tile-h:${g.h}px;` +
        `--avatar:${Math.round(Math.min(160, Math.max(32, Math.min(g.h * 0.42, g.w * 0.5))))}px`;
  const classes = ["grid", g?.compact && "compact", ids.length === 1 && "one"];
  // The server hears whose tiles are in sight and how large they are: a small one takes a small picture.
  useEffect(() => {
    if (g === null || visible === null) return;
    const seen = important(ids.filter((id) => visible.has(id)));
    showTiles(seen, seen.map(() => g.h));
  });
  // Tiles that fit: no scrolling at all. Rows that scroll: the scrollbar there all the time, so that
  // its coming and going does not change the pane's width, and the tiles with it.
  return (
    <div class={g?.scroll ? "people scrolls" : "people"} ref={pane}>
      <div class={classes.filter((x) => x).join(" ")} style={style}>
        {ids.map((id) => (
          <Tile key={id} id={id} />
        ))}
      </div>
    </div>
  );
}

/** The tile of user `id`: the picture of its camera, or its avatar — ringed while it speaks; its name, the
 * host's and the moderators' marks, bars while it speaks, the user's microphone off; and for the host the
 * buttons to mute and remove it — on small tiles under the pointer, or once the tile is touched. */
function Tile({ id }: { id: number }) {
  const [menu, setMenu] = useState(false);
  const m = state.meeting!;
  const owner = isHost();
  const own = id === state.user;
  const name = nameOf(id);
  const picture = videoOf(id);
  const speaks = state.speaking.has(id);
  const classes = ["tile", picture !== null && "video", speaks && "speaking", menu && "menu"];
  return (
    <div
      class={classes.filter((x) => x).join(" ")}
      data-user={id}
      onClick={owner && !own ? () => setMenu(!menu) : undefined}
    >
      {picture !== null ? (
        <Mount el={picture} />
      ) : (
        <span class="ring">
          <Avatar id={id} />
        </span>
      )}
      <div class="name">
        {id === m.host && <Icon name="crown" size={18} />}
        {isModerator(id) && <Icon name="shield" size={18} />}
        <span>{own ? `${name} (вы)` : name}</span>
        {own && state.micOff ? (
          <span class="muted-mic" title="Микрофон выключен">
            <Icon name="micOff" size={18} />
          </span>
        ) : (
          speaks && (
            <span class="talking" title="Говорит">
              <Icon name="bars" size={20} />
            </span>
          )
        )}
      </div>
      {owner && !own && (
        <div class="tile-acts">
          <IconButton
            icon="micOff"
            title="Выключить микрофон"
            onClick={(e) => {
              e.stopPropagation();
              host("mute", id);
            }}
          />
          <IconButton
            icon="userX"
            class="danger"
            title="Удалить из пространства"
            onClick={(e) => {
              e.stopPropagation();
              host("remove", id);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Element `el` — a video, which goes on playing as the page is drawn again — in a box of its own. */
function Mount({ el }: { el: HTMLVideoElement }) {
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const b = box.current!;
    if (el.parentNode !== b) b.replaceChildren(el);
    // Taken out of the page and put back, a video stops: it plays on.
    el.play().catch(() => {});
  }, [el]);
  return <div class="video-box" ref={box} />;
}
